import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Sparkles, Check, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import {
  apiClient,
  saveBirthDetails,
  getSessionId,
  computeProfileKey,
} from "@/lib/api";
import { track } from "@/lib/analytics";
import { CATEGORIES, sampleQuestions, buildProfile, profileToChallenge } from "@/lib/mcqBank";
import { useLang } from "@/lib/i18n";
import Sparks from "@/components/Sparks";

// With only 5 archetype questions, the full assessment is already fast — no
// separate "quick check-in" mode is needed. Kept as an empty stub for
// backwards-compatibility with older code paths.
const DELTA_QUESTION_INDICES = [];

export default function Onboarding() {
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const [step, setStep] = useState(1);
  const [birth, setBirth] = useState({
    full_name: "",
    date_of_birth: "",
    time_of_birth: "",
    place_of_birth: "",
    gender: "",
    whatsapp_number: "",
  });
  const [otp, setOtp] = useState({ sent: false, code: "", verified: false, sending: false, verifying: false, demoCode: null });
  const [category, setCategory] = useState("");
  const [answers, setAnswers] = useState({});
  const [qIndex, setQIndex] = useState(0);
  const [returningInfo, setReturningInfo] = useState(null);
  const [quickMode, setQuickMode] = useState(false);
  const [personalContext, setPersonalContext] = useState("");
  const [ctxOpen, setCtxOpen] = useState(false);

  // Rough word counter for the personal-context textarea (max 250 words).
  const ctxWordCount = personalContext.trim()
    ? personalContext.trim().split(/\s+/).length
    : 0;

  getSessionId();

  useEffect(() => {
    try {
      track("onboarding_start");
    } catch (_) {}
  }, []);

  // After step 2 category is set, detect if this identity already has a
  // previous reading and surface a "Quick check-in" offer.
  useEffect(() => {
    if (step !== 2 || !category) return;
    if (!birth.full_name || !birth.date_of_birth) return;
    const pk = computeProfileKey(birth);
    (async () => {
      try {
        const res = await apiClient.get(
          `/consultations/previous-profile?profile_key=${encodeURIComponent(pk)}`,
          { skipPaywall: true }
        );
        if (res.data?.has_previous) setReturningInfo(res.data);
        else setReturningInfo(null);
      } catch (_) {
        setReturningInfo(null);
      }
    })();
  }, [step, category, birth.full_name, birth.date_of_birth, birth.time_of_birth, birth.place_of_birth]);

  // Sample 5 fresh questions per (category, language, session). We seed once
  // per session so re-renders don't re-shuffle mid-flow, but every fresh
  // visit gets a new draw from the 20-question pool per domain.
  const sessionSeed = useMemo(
    () => Math.floor(Math.random() * 1e9) + 1,
    []
  );
  const questions = useMemo(() => {
    if (!category) return [];
    return sampleQuestions(category, lang, sessionSeed);
  }, [category, lang, sessionSeed]);
  const activeQuestions = useMemo(
    () => (quickMode ? DELTA_QUESTION_INDICES.map((i) => questions[i]).filter(Boolean) : questions),
    [questions, quickMode]
  );
  const totalQ = activeQuestions.length;
  const answeredCount = Object.keys(answers).filter((k) => answers[k] !== undefined).length;

  const sendOtp = async () => {
    if (!birth.whatsapp_number.trim()) {
      toast.error(t("onb.whatsapp") + " —");
      return;
    }
    setOtp({ ...otp, sending: true });
    try {
      const res = await apiClient.post(
        "/whatsapp/send-otp",
        { phone_number: birth.whatsapp_number },
        { skipPaywall: true }
      );
      const demoCode = res.data?.demo_code || null;
      setOtp({ sent: true, code: "", verified: false, sending: false, verifying: false, demoCode });
      toast.success(
        demoCode ? `${t("onb.whatsapp.demoCode")}: ${demoCode}` : t("onb.whatsapp.sending")
      );
    } catch (e) {
      setOtp({ ...otp, sending: false });
      toast.error(t("err.network.title"));
    }
  };

  const verifyOtp = async () => {
    if (!otp.code || otp.code.length < 4) {
      toast.error(t("onb.whatsapp.enterOtp"));
      return;
    }
    setOtp({ ...otp, verifying: true });
    try {
      await apiClient.post(
        "/whatsapp/verify-otp",
        { phone_number: birth.whatsapp_number, code: otp.code },
        { skipPaywall: true }
      );
      setOtp({ ...otp, verifying: false, verified: true });
      toast.success(t("onb.whatsapp.verified"));
    } catch (e) {
      setOtp({ ...otp, verifying: false });
      toast.error(e?.response?.data?.detail || t("onb.whatsapp.enterOtp"));
    }
  };

  const selectOption = (optIdx) => {
    setAnswers((prev) => ({ ...prev, [qIndex]: optIdx }));
    setTimeout(() => {
      if (qIndex < totalQ - 1) setQIndex(qIndex + 1);
    }, 280);
  };

  const next = () => {
    if (step === 1) {
      const requiredKeys = ["full_name", "date_of_birth", "time_of_birth", "place_of_birth", "gender", "whatsapp_number"];
      const missing = requiredKeys.filter((k) => !(birth[k] || "").trim());
      if (missing.length) {
        toast.error(t("onb.errors.missingBirth"));
        return;
      }
      if (!otp.verified) {
        toast.error(t("onb.whatsapp.verify"));
        return;
      }
      saveBirthDetails(birth);
      setStep(2);
      return;
    }
    if (step === 2) {
      if (!category) {
        toast.error(t("onb.errors.needCategory"));
        return;
      }
      setStep(3);
      setQIndex(0);
      return;
    }
    if (answeredCount < totalQ) {
      toast.error(t("onb.errors.answerAll"));
      return;
    }
    // Build the psychological profile from the 5 answers, using the exact
    // questions the user saw (needed because pool sampling means each visit
    // shows a different 5-question set).
    const answersArr = activeQuestions.map((_, i) => answers[i]);
    const profile = buildProfile(category, answersArr, activeQuestions);
    const trimmedCtx = (personalContext || "").trim().slice(0, 4000);
    const challenge = trimmedCtx || profileToChallenge(category, profile);
    try {
      track("onboarding_complete", {
        properties: { category, has_personal_context: !!trimmedCtx },
      });
    } catch (_) {}
    navigate("/processing", {
      state: {
        birth,
        category,
        challenge,
        profile,
        language: lang,
        personal_context: trimmedCtx,
      },
    });
  };

  const back = () => {
    if (step === 3 && qIndex > 0) {
      setQIndex(qIndex - 1);
      return;
    }
    setStep(Math.max(1, step - 1));
  };

  return (
    <div className="min-h-screen cosmic-bg flex flex-col">
      <Sparks />
      <header className="max-w-7xl w-full mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2"
          data-testid="onboarding-home-link"
        >
          <Sparkles className="w-5 h-5 gold-text" strokeWidth={1.5} />
          <span className="font-serif text-xl text-white">
            {lang === "hi" ? (
              <>मंत्रणा<span className="gold-text"> One</span></>
            ) : (
              <>Mantrana<span className="gold-text"> One</span></>
            )}
          </span>
        </button>
        <div className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-slate-400">
          {t("onb.step.phase", { n: step })}
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-2xl w-full">
          <div className="flex gap-2 mb-12" data-testid="onboarding-progress">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className="h-0.5 flex-1 rounded-full transition-colors"
                style={{ background: s <= step ? "#D4AF37" : "rgba(255,255,255,0.1)" }}
              />
            ))}
          </div>

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="s1"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4 }}
                data-testid="onboarding-step-1"
              >
                <div className="text-xs tracking-[0.25em] uppercase gold-text mb-4">
                  {t("onb.step1.eyebrow")}
                </div>
                <h1 className="font-serif text-4xl md:text-5xl font-light text-white mb-3">
                  {t("onb.step1.title.a")} <span className="italic gold-text">{t("onb.step1.title.b")}</span>
                </h1>
                <p className="text-slate-400 font-light mb-10">
                  {t("onb.step1.subtitle")}
                </p>
                <div className="space-y-5">
                  <div>
                    <Label className="text-slate-300 text-sm">{t("onb.fullName")}</Label>
                    <Input
                      className="mt-2 bg-white/5 border-white/10 text-white h-12"
                      value={birth.full_name}
                      onChange={(e) => setBirth({ ...birth, full_name: e.target.value })}
                      placeholder={t("onb.fullName.placeholder")}
                      data-testid="input-full-name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300 text-sm">{t("onb.dob")}</Label>
                      <Input
                        type="date"
                        className="mt-2 bg-white/5 border-white/10 text-white h-12"
                        value={birth.date_of_birth}
                        onChange={(e) => setBirth({ ...birth, date_of_birth: e.target.value })}
                        data-testid="input-dob"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">{t("onb.tob")}</Label>
                      <Input
                        type="time"
                        className="mt-2 bg-white/5 border-white/10 text-white h-12"
                        value={birth.time_of_birth}
                        onChange={(e) => setBirth({ ...birth, time_of_birth: e.target.value })}
                        data-testid="input-tob"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">{t("onb.pob")}</Label>
                    <Input
                      className="mt-2 bg-white/5 border-white/10 text-white h-12"
                      value={birth.place_of_birth}
                      onChange={(e) => setBirth({ ...birth, place_of_birth: e.target.value })}
                      placeholder={t("onb.pob.placeholder")}
                      data-testid="input-pob"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">{t("onb.gender")}</Label>
                    <Select
                      value={birth.gender}
                      onValueChange={(v) => setBirth({ ...birth, gender: v })}
                    >
                      <SelectTrigger
                        className="mt-2 bg-white/5 border-white/10 text-white h-12"
                        data-testid="input-gender"
                      >
                        <SelectValue placeholder={t("onb.gender.select")} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#13192B] text-white border-white/10">
                        <SelectItem value="female">{t("onb.gender.female")}</SelectItem>
                        <SelectItem value="male">{t("onb.gender.male")}</SelectItem>
                        <SelectItem value="non-binary">{t("onb.gender.other")}</SelectItem>
                        <SelectItem value="prefer-not-to-say">{t("onb.gender.preferNot")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* WhatsApp verification segment */}
                  <div
                    className="rounded-2xl p-5 mt-2"
                    style={{
                      background: "rgba(212,175,55,0.05)",
                      border: "1px solid rgba(212,175,55,0.22)",
                    }}
                    data-testid="whatsapp-section"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <MessageCircle className="w-4 h-4 gold-text" strokeWidth={1.5} />
                      <Label className="text-slate-300 text-sm">
                        {t("onb.whatsapp")}{" "}
                        <span className="text-[10px] tracking-[0.2em] uppercase gold-text ml-1">
                          {t("onb.whatsapp.forCode")}
                        </span>
                      </Label>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        className="bg-white/5 border-white/10 text-white h-12 flex-1"
                        value={birth.whatsapp_number}
                        onChange={(e) =>
                          setBirth({ ...birth, whatsapp_number: e.target.value })
                        }
                        placeholder={t("onb.whatsapp.placeholder")}
                        disabled={otp.verified}
                        data-testid="input-whatsapp"
                      />
                      {!otp.verified && (
                        <button
                          type="button"
                          onClick={sendOtp}
                          disabled={otp.sending || !birth.whatsapp_number.trim()}
                          className="rounded-xl px-4 text-xs font-medium border whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                          style={{
                            background: otp.sent ? "rgba(255,255,255,0.04)" : "rgba(212,175,55,0.12)",
                            borderColor: "rgba(212,175,55,0.4)",
                            color: "#FBE38E",
                          }}
                          data-testid="whatsapp-send-button"
                        >
                          {otp.sending
                            ? t("onb.whatsapp.sending")
                            : otp.sent
                            ? t("onb.whatsapp.resend")
                            : t("onb.whatsapp.send")}
                        </button>
                      )}
                    </div>
                    {otp.demoCode && !otp.verified && (
                      <p className="mt-2 text-[11px] text-amber-300/80">
                        {t("onb.whatsapp.demoCode")}:{" "}
                        <span className="font-serif text-amber-300">{otp.demoCode}</span>
                      </p>
                    )}
                    {otp.sent && !otp.verified && (
                      <div className="flex gap-2 mt-3">
                        <Input
                          className="bg-white/5 border-white/10 text-white h-11 flex-1"
                          value={otp.code}
                          onChange={(e) =>
                            setOtp({ ...otp, code: e.target.value.replace(/\D/g, "").slice(0, 6) })
                          }
                          placeholder={t("onb.whatsapp.enterOtp")}
                          inputMode="numeric"
                          data-testid="input-otp"
                        />
                        <button
                          type="button"
                          onClick={verifyOtp}
                          disabled={otp.verifying || otp.code.length < 4}
                          className="rounded-xl px-4 text-xs font-medium cta-gold disabled:opacity-40 whitespace-nowrap"
                          data-testid="whatsapp-verify-button"
                        >
                          {otp.verifying ? t("onb.whatsapp.sending") : t("onb.whatsapp.verify")}
                        </button>
                      </div>
                    )}
                    {otp.verified && (
                      <div
                        className="mt-2 inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase"
                        style={{ color: "#34d399" }}
                        data-testid="whatsapp-verified-pill"
                      >
                        <Check className="w-3 h-3" strokeWidth={2.5} />
                        {t("onb.whatsapp.verified")}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="s2"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4 }}
                data-testid="onboarding-step-2"
              >
                <div className="text-xs tracking-[0.25em] uppercase gold-text mb-4">
                  {t("onb.step2.eyebrow")}
                </div>
                <h1 className="font-serif text-4xl md:text-5xl font-light text-white mb-3">
                  {t("onb.step2.title.a")}{" "}
                  <span className="italic gold-text">{t("onb.step2.title.b")}</span>
                </h1>
                <p className="text-slate-400 font-light mb-10">
                  {t("onb.step2.subtitle")}
                </p>
                {returningInfo && returningInfo.category === category && (
                  <div
                    className="rounded-2xl p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4"
                    style={{
                      background: "rgba(212,175,55,0.08)",
                      border: "1px solid rgba(212,175,55,0.35)",
                    }}
                    data-testid="quick-checkin-offer"
                  >
                    <div className="flex-1">
                      <div className="text-[10px] tracking-[0.25em] uppercase gold-text mb-1">
                        {t("onb.returning.eyebrow")}
                      </div>
                      <p className="font-serif text-base text-white leading-snug">
                        {t("onb.returning.body.a")}{" "}
                        {t(`cat.${category}`)}{" "}
                        {t("onb.returning.body.b")}{" "}
                        <span className="gold-text">
                          {t("onb.returning.body.highlight")}
                        </span>
                        {" "}{t("onb.returning.body.c")}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => setQuickMode(true)}
                        className={`rounded-full px-4 py-2 text-xs font-medium ${
                          quickMode ? "cta-gold" : "border border-white/15 hover:bg-white/5 text-white"
                        }`}
                        data-testid="quick-checkin-yes"
                      >
                        {t("onb.returning.quick")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setQuickMode(false)}
                        className={`rounded-full px-4 py-2 text-xs font-medium ${
                          !quickMode ? "cta-gold" : "border border-white/15 hover:bg-white/5 text-white"
                        }`}
                        data-testid="quick-checkin-no"
                      >
                        {t("onb.returning.full")}
                      </button>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {CATEGORIES.map((c) => {
                    const active = category === c;
                    return (
                      <button
                        key={c}
                        onClick={() => setCategory(c)}
                        className="rounded-2xl px-5 py-5 text-left font-light transition-all border"
                        style={{
                          background: active ? "rgba(212,175,55,0.12)" : "rgba(255,255,255,0.03)",
                          borderColor: active ? "#D4AF37" : "rgba(255,255,255,0.08)",
                          color: active ? "#fff" : "#cbd5e1",
                        }}
                        data-testid={`category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {t(`cat.${c}`)}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <>
                <motion.div
                  key="s3"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.4 }}
                  data-testid="onboarding-step-3"
                >
                <div className="text-xs tracking-[0.25em] uppercase gold-text mb-4">
                  {quickMode
                    ? t("onb.step3.eyebrow.quick")
                    : t("onb.step3.eyebrow")}{" "}
                  · {t(`cat.${category}`)}
                </div>
                {/* Question progress dots */}
                <div className="flex items-center gap-1.5 mb-6" data-testid="mcq-progress">
                  {activeQuestions.map((_, i) => (
                    <div
                      key={i}
                      className="h-1 flex-1 rounded-full transition-colors"
                      style={{
                        background:
                          i === qIndex
                            ? "#D4AF37"
                            : answers[i] !== undefined
                            ? "rgba(212,175,55,0.5)"
                            : "rgba(255,255,255,0.08)",
                      }}
                    />
                  ))}
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={qIndex}
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="text-[11px] tracking-[0.25em] uppercase text-slate-500 mb-3">
                      {t("onb.step3.progress", { i: qIndex + 1, n: totalQ })}
                    </div>
                    <h2
                      className="font-serif text-2xl md:text-3xl font-light text-white mb-8 leading-snug"
                      data-testid="mcq-question"
                    >
                      {activeQuestions[qIndex]?.q}
                    </h2>
                    <div className="space-y-3">
                      {(activeQuestions[qIndex]?.options || []).map((opt, i) => {
                        const selected = answers[qIndex] === i;
                        return (
                          <button
                            key={i}
                            onClick={() => selectOption(i)}
                            className="w-full text-left rounded-2xl px-5 py-4 font-light transition-all border flex items-center justify-between gap-4"
                            style={{
                              background: selected
                                ? "rgba(212,175,55,0.12)"
                                : "rgba(255,255,255,0.03)",
                              borderColor: selected ? "#D4AF37" : "rgba(255,255,255,0.08)",
                              color: selected ? "#fff" : "#e2e8f0",
                            }}
                            data-testid={`mcq-option-${i}`}
                          >
                            <span className="leading-snug">{opt.text}</span>
                            {selected && (
                              <Check
                                className="w-4 h-4 shrink-0"
                                style={{ color: "#D4AF37" }}
                                strokeWidth={2.5}
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {/* Question navigation */}
                    <div className="flex justify-between items-center mt-8">
                      <button
                        onClick={() => setQIndex(Math.max(0, qIndex - 1))}
                        disabled={qIndex === 0}
                        className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                        data-testid="mcq-back"
                      >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        {t("common.previous")}
                      </button>
                      <div className="text-[10px] tracking-[0.22em] uppercase text-slate-500">
                        {t("onb.step3.answered", { a: answeredCount, n: totalQ })}
                      </div>
                      <button
                        onClick={() =>
                          qIndex < totalQ - 1 ? setQIndex(qIndex + 1) : null
                        }
                        disabled={
                          answers[qIndex] === undefined || qIndex === totalQ - 1
                        }
                        className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                        data-testid="mcq-next"
                      >
                        {t("common.next")}
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </motion.div>

              {/* ─── OPTIONAL PERSONAL CONTEXT ────────────────────────── */}
              {step === 3 && answeredCount === totalQ && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="mt-6 glass rounded-3xl p-8 md:p-10"
                  style={{ border: "1px solid rgba(212,175,55,0.22)" }}
                  data-testid="personal-context-card"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="text-[10px] tracking-[0.25em] uppercase gold-text mb-3">
                        {t("ctx.eyebrow")}
                      </div>
                      <h3 className="font-serif text-2xl md:text-3xl font-light text-white leading-snug">
                        {t("ctx.title")}
                      </h3>
                      <p className="mt-3 report-body text-slate-300 max-w-2xl">
                        {t("ctx.subtitle")}
                      </p>
                    </div>
                    {!ctxOpen && (
                      <button
                        type="button"
                        onClick={() => setCtxOpen(true)}
                        className="shrink-0 rounded-full px-4 py-2 text-xs font-medium border border-white/15 hover:bg-white/5 text-white whitespace-nowrap"
                        data-testid="personal-context-reveal"
                      >
                        {t("ctx.reveal")}
                      </button>
                    )}
                  </div>

                  <AnimatePresence>
                    {ctxOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                      >
                        <div className="mt-6">
                          <textarea
                            value={personalContext}
                            onChange={(e) => {
                              const words = e.target.value.trim()
                                ? e.target.value.trim().split(/\s+/)
                                : [];
                              // hard-cap at 250 words
                              if (words.length <= 250) {
                                setPersonalContext(e.target.value);
                              } else {
                                setPersonalContext(words.slice(0, 250).join(" "));
                              }
                            }}
                            rows={7}
                            placeholder={t("ctx.placeholder")}
                            className="w-full rounded-2xl px-5 py-4 report-body text-slate-100 outline-none transition-all resize-y min-h-[160px]"
                            style={{
                              background: "rgba(255,255,255,0.03)",
                              border: "1px solid rgba(255,255,255,0.10)",
                              lineHeight: 1.7,
                            }}
                            data-testid="personal-context-textarea"
                          />
                          <div className="mt-3 flex items-center justify-between text-[11px]">
                            <span className="text-slate-500 italic" data-testid="personal-context-helper">
                              {t("ctx.helper")}
                            </span>
                            <span
                              className={`tracking-wide tabular-nums ${
                                ctxWordCount > 220 ? "gold-text" : "text-slate-500"
                              }`}
                              data-testid="personal-context-count"
                            >
                              {t("ctx.wordCount", { n: ctxWordCount })}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}
              </>
            )}
          </AnimatePresence>

          <div className="flex justify-between items-center mt-12">
            <button
              onClick={back}
              disabled={step === 1 && qIndex === 0}
              className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
              data-testid="onboarding-back-button"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("common.back")}
            </button>
            <button
              onClick={next}
              disabled={step === 3 && answeredCount < totalQ}
              className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-medium transition-all hover:scale-[1.02] gold-glow disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: "#D4AF37", color: "#0B1020" }}
              data-testid="onboarding-next-button"
            >
              {step === 3
                ? answeredCount < totalQ
                  ? t("onb.step3.answerMore", { n: totalQ - answeredCount })
                  : t("onb.step3.finish")
                : t("common.continue")}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
